import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const DEBUG_VARIANT_SWITCH = new URLSearchParams(window.location.search).has('debug_variant');

/** @param {string} message */
function debugVariantLog(message) {
  if (!DEBUG_VARIANT_SWITCH) return;
  console.log('[sticky-variant-switch]', message);
  let panel = document.getElementById('sticky-variant-debug');
  if (!panel) {
    panel = document.createElement('div');
    panel.id = 'sticky-variant-debug';
    panel.style.cssText =
      'position:fixed;top:10px;left:10px;z-index:2147483647;background:#000;color:#0ff;' +
      'font:11px/1.4 monospace;padding:10px;max-width:360px;max-height:280px;overflow:auto;' +
      'white-space:pre-wrap;border-radius:6px;box-shadow:0 4px 20px rgba(0,0,0,0.5);';
    document.body.appendChild(panel);
  }
  const line = document.createElement('div');
  line.textContent = new Date().toISOString().slice(11, 23) + '  ' + message;
  panel.appendChild(line);
  panel.scrollTop = panel.scrollHeight;
}

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {'auto' | 'scroll_position'} */
  #triggerMode = 'auto';

  /** @type {number} */
  #scrollThreshold = 600;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {boolean} */
  #dismissed = false;

  /** @type {number} */
  #dismissResetThreshold = 24;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#scrollRaf !== null) {
      cancelAnimationFrame(this.#scrollRaf);
    }
  }

  /**
   * Sets up the visibility trigger, either watching the buy buttons
   * (default) or a fixed scroll position, depending on `data-trigger-mode`.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    this.#buyButtonsBlock = /** @type {HTMLElement | null} */ (buyButtonsBlock);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    this.#triggerMode = this.dataset.triggerMode === 'scroll_position' ? 'scroll_position' : 'auto';
    this.#scrollThreshold = parseInt(this.dataset.triggerScrollThreshold || '600', 10) || 600;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    if (this.#triggerMode === 'scroll_position') {
      this.#handleScroll();
    } else if (buyButtonsBlock) {
      // Observer for buy buttons visibility
      this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (!entry) return;

        // Only show sticky bar if buy buttons have been scrolled past (above viewport)
        if (!entry.isIntersecting && !this.#isStuck) {
          // Check if the element is above the viewport (scrolled past) or below (not yet reached)
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            if (this.#isChatActive()) return;
            this.#showStickyBar();
          }
          // If rect.top >= 0, element is below viewport - don't show sticky bar yet
        } else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      });

      this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    }

    if (!footer) return;

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const shouldReshow = this.#shouldBeVisible();
          if (shouldReshow) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#mainBottomObserver.observe(footer);
  }

  /**
   * Whether the sticky bar's trigger condition is currently met, per the
   * active trigger mode. Used by the footer re-show check for both modes.
   * @returns {boolean}
   */
  #shouldBeVisible() {
    if (this.#triggerMode === 'scroll_position') {
      return window.scrollY >= this.#scrollThreshold;
    }
    if (!this.#buyButtonsBlock) return false;
    const rect = this.#buyButtonsBlock.getBoundingClientRect();
    return rect.bottom < 0 || rect.top < 0;
  }

  /** @type {number | null} */
  #scrollRaf = null;

  /**
   * Runs on every scroll (throttled to one check per animation frame):
   * - Resets the "dismissed" state once the shopper scrolls back near the
   *   top of the page, so the bar can reappear on the next scroll-down.
   * - In `scroll_position` trigger mode, shows/hides the bar based on the
   *   configured scroll threshold.
   */
  #handleScroll = () => {
    if (this.#scrollRaf !== null) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;

      if (this.#dismissed && window.scrollY <= this.#dismissResetThreshold) {
        this.#dismissed = false;
      }

      if (this.#triggerMode !== 'scroll_position') return;
      if (this.#hiddenByBottom) return;

      const pastThreshold = window.scrollY >= this.#scrollThreshold;
      if (pastThreshold && !this.#isStuck) {
        if (this.#isChatActive()) return;
        this.#showStickyBar();
      } else if (!pastThreshold && this.#isStuck) {
        this.#hideStickyBar();
      }
    });
  };

  // Public action handlers
  /**
   * Handles a click on the sticky bar's dismiss ("X") button: hides the bar
   * and suppresses it until the shopper scrolls back near the top of the
   * page, at which point it can appear again on the next scroll-down.
   */
  handleDismiss = () => {
    this.#dismissed = true;
    this.#hiddenByBottom = false;
    this.#hideStickyBar();
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles a click on one of the sticky bar's variant switcher pills.
   * Finds the matching radio input rendered by the page's real variant
   * picker (identified by its `data-option-value-id`) and checks it,
   * dispatching a `change` event so the theme's own variant-picker sync
   * (price, buy-buttons, sticky bar, accelerated checkout) runs normally.
   * No custom cart/checkout logic is involved.
   * @param {MouseEvent} event
   */
  handleVariantSwitch = (event) => {
    // The theme's on:click system delegates via a single document-level
    // listener, so `event.currentTarget` doesn't point at the clicked pill
    // (that's what caused the "pill?.getAttribute is not a function" error).
    // `event.target` reflects the actual click origin regardless of
    // delegation, so resolve the pill from there instead.
    const pill = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.sticky-add-to-cart__switcher-pill') : null
    );
    debugVariantLog(`click received. event.target=${/** @type {Element} */ (event.target)?.tagName} pill=${pill?.tagName} aria-disabled=${pill?.getAttribute('aria-disabled')}`);
    if (!pill || pill.getAttribute('aria-disabled') === 'true') {
      debugVariantLog('ABORT: no pill or pill is aria-disabled.');
      return;
    }

    const optionValueId = pill.dataset.optionValueId;
    debugVariantLog(`optionValueId=${optionValueId}`);
    if (!optionValueId) {
      debugVariantLog('ABORT: pill has no data-option-value-id.');
      return;
    }

    const matches = document.querySelectorAll(`input[data-option-value-id="${optionValueId}"]`);
    debugVariantLog(`found ${matches.length} input(s) with this data-option-value-id on the page.`);
    matches.forEach((el, i) => {
      const inp = /** @type {HTMLInputElement} */ (el);
      debugVariantLog(
        `  [${i}] disabled=${inp.disabled} checked=${inp.checked} closest(variant-picker)=${Boolean(inp.closest('variant-picker'))} name=${inp.name}`
      );
    });

    const input = /** @type {HTMLInputElement | null} */ (matches[0] ?? null);
    if (!input || input.disabled) {
      debugVariantLog('ABORT: no matching input found, or it is disabled.');
      return;
    }

    input.checked = true;
    const dispatched = input.dispatchEvent(new Event('change', { bubbles: true }));
    debugVariantLog(`set checked=true and dispatched 'change' event. dispatchEvent returned=${dispatched}`);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    if (this.#dismissed) return;
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
